import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { useUser, useAuth } from "@/hooks/auth";
import { Mail, RefreshCw, HelpCircle } from "lucide-react";
import { InlineLoader } from '@/components/ui/loading';
import { useTranslation } from "@/hooks/useTranslation";
import { LanguageSelector } from "@/components/auth/LanguageSelector";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { DisplayMedium, HeadingMedium, BodyMedium, BodySmall, Caption } from '@/components/ui/typography';
import { SharedAuthForm } from "@/components/auth/SharedAuthForm";

const Auth = () => {
  const { t } = useTranslation('auth');
  const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [lastResendTime, setLastResendTime] = useState<number | null>(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [sendingTicket, setSendingTicket] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useTranslatedToast();
  
  // Use optimized auth hooks
  const { user } = useUser();
  const { isDataLoading: authLoading } = useAuth();

  // Image sources to preload
  const imagesToPreload = [
    "/lovable-uploads/41653ae6-6c86-4657-96f9-4685be3c3e81.png",
    "/lovable-uploads/fc11f719-705d-4b9e-93ba-b8b240bbd390.png"
  ];

  useEffect(() => {
    // Preload images
    const preloadImages = async () => {
      try {
        const imagePromises = imagesToPreload.map((src) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = reject;
            img.src = src;
          });
        });

        await Promise.all(imagePromises);
        setImagesLoaded(true);
      } catch (error) {
        console.warn('Some images failed to preload:', error);
        setImagesLoaded(true);
      }
    };

    preloadImages();
  }, []);

  // Handle URL params and auth redirect logic
  useEffect(() => {
    // Handle OAuth error/success messages from URL params
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const errorCode = searchParams.get('error_code');
    const message = searchParams.get('message');

    if (error) {
      let title = "Authentication Error";
      let description = errorDescription || "Failed to authenticate";

      if (errorCode === 'otp_expired') {
        title = "Magic Link Expired";
        description = "The magic link has expired. Please request a new one.";
      }

      toast.raw.error(description, { description: title });
    } else if (message) {
      toast.raw.success(message, { description: "Success" });
    }
  }, [searchParams, toast]);

  // If a recovery flow is detected in URL, route to the reset-password page
  useEffect(() => {
    const type = searchParams.get('type');
    const code = searchParams.get('code');
    if (type === 'recovery' || code) {
      const to = '/reset-password' + window.location.search + window.location.hash;
      navigate(to, { replace: true });
    }
  }, [searchParams, navigate]);

  // Auto-check email confirmation status every 3 seconds when pending
  useEffect(() => {
    if (emailConfirmationSent && pendingEmail) {
      const interval = setInterval(checkEmailConfirmation, 3000);
      return () => clearInterval(interval);
    }
  }, [emailConfirmationSent, pendingEmail]);

  // Email confirmation resend functionality
  const handleResendConfirmation = async () => {
    const now = Date.now();
    if (lastResendTime && (now - lastResendTime) < 60000) {
      const remainingTime = Math.ceil((60000 - (now - lastResendTime)) / 1000);
      toast.error(`Please wait ${remainingTime} seconds before requesting another confirmation email.`);
      return;
    }

    setResendLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: pendingEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        toast.error("Failed to resend confirmation email. Please try again.");
      } else {
        setLastResendTime(now);
        toast.success("Confirmation email sent!", {
          description: "Please check your inbox and spam folder."
        });
      }
    } catch (error) {
      toast.error("An error occurred while resending the confirmation email.");
    } finally {
      setResendLoading(false);
    }
  };

  // Check if email has been confirmed (called periodically)
  const checkEmailConfirmation = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && user.email_confirmed_at) {
        setEmailConfirmationSent(false);
        setPendingEmail("");
        toast.success("Email confirmed! Redirecting to your account...");
        navigate("/", { replace: true });
      }
    } catch (error) {
      // Silently fail - user might not be signed in yet
    }
  };

  // Send support ticket
  const handleSendSupportTicket = async () => {
    setSendingTicket(true);
    try {
      const { error } = await supabase.functions.invoke('send-support-ticket', {
        body: {
          email: pendingEmail,
          issue: 'Email confirmation not received',
          userAgent: navigator.userAgent,
          url: window.location.href
        }
      });

      if (error) throw error;
      
      toast.success("Support ticket sent! We'll help you shortly.");
      setHelpDialogOpen(false);
    } catch (error) {
      console.error('Error sending support ticket:', error);
      toast.error("Failed to send support ticket. Please try again.");
    } finally {
      setSendingTicket(false);
    }
  };

  // Email Confirmation Pending Screen
  const EmailConfirmationPending = () => (
    <div className="w-full max-w-md mx-auto space-y-6">
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          <Mail className="w-8 h-8 text-primary" />
        </div>
        <div className="space-y-2">
          <DisplayMedium className="text-foreground">{t('email_confirmation.title')}</DisplayMedium>
          <div className="space-y-1">
            <BodyMedium className="text-muted-foreground">
              We've sent a confirmation link to:
            </BodyMedium>
            <BodyMedium className="font-medium text-foreground break-all">{pendingEmail}</BodyMedium>
          </div>
            <BodySmall className="text-muted-foreground">
              Click the link in your email to confirm your account. Check your spam folder if you don't see it.
            </BodySmall>
        </div>
      </div>

      <div className="space-y-3">
        <Button 
          onClick={handleResendConfirmation}
          disabled={resendLoading}
          variant="outline"
          className="w-full"
          title="Resend confirmation email (only works if email is unconfirmed)"
        >
          {resendLoading ? (
            <InlineLoader size="sm" text="Sending..." />
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Resend Email
            </>
          )}
        </Button>

        <Dialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="ghost" className="w-full">
              <HelpCircle className="w-4 h-4 mr-2" />
              Get Help
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Need Help?</DialogTitle>
              <DialogDescription>
                Having trouble receiving your confirmation email? We'll send a support ticket to help you.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg text-sm">
                <p className="font-medium">Your email:</p>
                <p className="text-muted-foreground break-all">{pendingEmail}</p>
              </div>
              <Button 
                onClick={handleSendSupportTicket}
                disabled={sendingTicket}
                className="w-full"
              >
                {sendingTicket ? (
                  <InlineLoader size="sm" text="Sending..." />
                ) : (
                  'Send Support Ticket'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full">
      {/* Left Side - Feature Showcase (Fixed) */}
      <div className="hidden lg:block lg:absolute lg:left-0 lg:right-[480px] xl:right-[560px] bg-card dark:bg-surface-tertiary top-0 h-screen overflow-hidden z-40 shadow-[4px_0_24px_-8px_rgba(0,0,0,0.1)] dark:shadow-[4px_0_24px_-8px_rgba(0,0,0,0.3)]">
        <div className="h-full flex flex-col justify-center items-center p-8 text-foreground">
          <div className="max-w-lg w-full text-left mb-8">
            <HeadingMedium className="mb-4 text-foreground">{t('brand_message.title')}</HeadingMedium>
            <BodySmall className="text-muted-foreground dark:text-muted-foreground">{t('brand_message.description')}</BodySmall>
          </div>
          <div className="max-w-lg w-full">
            <img 
              src="/lovable-uploads/fc11f719-705d-4b9e-93ba-b8b240bbd390.png" 
              alt="AI screening feature" 
              className="w-full h-auto rounded-lg shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] dark:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)]"
              loading="eager"
            />
          </div>
        </div>
      </div>

      {/* Right Side - Form (Independent) */}
      <div className="w-full lg:w-[480px] xl:w-[560px] lg:ml-auto min-h-screen flex flex-col bg-background dark:bg-background overflow-hidden">
        {/* Header with Logo and Controls - Fixed */}
        <div className="sticky top-0 z-30 flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-all duration-300 ease-in-out">
          <a 
            href={window.location.origin} 
            className="block hover:opacity-80 transition-opacity"
            aria-label="Go to home page"
          >
            <img 
              src="/lovable-uploads/41653ae6-6c86-4657-96f9-4685be3c3e81.png" 
              alt="Evee AI Logo" 
              className="w-8 h-8 object-contain rounded-lg"
            />
          </a>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LanguageSelector />
          </div>
        </div>
        
        {/* Main Content - Optical centering */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8 sm:py-12 min-h-0">
          <div className="w-full max-w-sm mx-auto px-2 sm:px-0 -mt-[6vh] sm:-mt-[8vh] lg:-mt-[10vh]">
            {emailConfirmationSent ? (
              <EmailConfirmationPending />
            ) : (
              <SharedAuthForm 
                compact={false}
                redirectAfterAuth={true}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
